<template>
    <!-- ── text / number / date ─────────────────────────────────────────────── -->
    <v-text-field v-if="isTextField" :model-value="modelValue" :label="campo.etichetta" :type="inputType" :rules="rules"
        hide-details="auto" density="compact" variant="outlined"
        @update:model-value="$emit('update:modelValue', $event)">
        <template #prepend-inner v-if="iconaCampo">
            <v-icon size="16" class="text-medium-emphasis">{{ iconaCampo }}</v-icon>
        </template>
    </v-text-field>

    <!-- ── textarea ─────────────────────────────────────────────────────────── -->
    <v-textarea v-else-if="campo.tipoInput === 'textarea'" :model-value="modelValue" :label="campo.etichetta"
        :rules="rules" hide-details="auto" density="compact" variant="outlined" :rows="2" autoGrow :max-rows="6"
        @update:model-value="$emit('update:modelValue', $event)" />

    <!-- ── select ───────────────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'select'">
        <!-- Verniciatura: campo testo + menu dropdown (comportamento speciale) -->
        <template v-if="campo.campoKey === 'Verniciatura'">
            <v-row dense>
                <v-col cols="12" sm="8">
                    <v-text-field :model-value="modelValue" :label="campo.etichetta" density="compact"
                        variant="outlined" hide-details @update:model-value="$emit('update:modelValue', $event)" />
                </v-col>
                <v-col cols="12" sm="4">
                    <v-menu>
                        <template #activator="{ props }">
                            <v-btn v-bind="props" block variant="outlined" prepend-icon="mdi-menu-down"
                                density="compact">
                                Elenco
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item v-for="item in opzioniCampo" :key="item.value"
                                @click="$emit('update:modelValue', item.value)">
                                <v-list-item-title>{{ item.label }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>
            </v-row>
        </template>

        <!-- Select con valore custom abilitato -->
        <template v-else-if="campo.permettiValoreCustom">
            <v-select :model-value="modalitaCustom ? VALORE_ALTRO : modelValue" :items="opzioniConAltro"
                item-value="value" item-title="label" :label="campo.etichetta" :rules="rules" hide-details="auto"
                density="compact" variant="outlined" @update:model-value="onSelectConCustom" />
            <v-text-field v-if="modalitaCustom" :model-value="modelValue"
                :label="`${campo.etichetta} (valore personalizzato)`" density="compact" variant="outlined"
                hide-details="auto" class="mt-2" clearable @update:model-value="$emit('update:modelValue', $event)"
                @click:clear="onRipristinaSelect">
                <template #prepend-inner>
                    <v-icon size="15" color="warning">mdi-pencil-outline</v-icon>
                </template>
            </v-text-field>
        </template>

        <!-- Select standard -->
        <v-select v-else :model-value="modelValue" :items="opzioniCampo" item-value="value" item-title="label"
            :label="campo.etichetta" :rules="rules" hide-details="auto" density="compact" variant="outlined"
            @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- ── autocomplete ─────────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'autocomplete'">
        <!-- Autocomplete con valore custom abilitato -->
        <template v-if="campo.permettiValoreCustom">
            <v-autocomplete :model-value="modalitaCustom ? VALORE_ALTRO : modelValue" :items="opzioniConAltro"
                item-value="value" item-title="label" :label="campo.etichetta" :rules="rules" hide-details="auto"
                density="compact" variant="outlined" @update:model-value="onSelectConCustom" />
            <v-text-field v-if="modalitaCustom" :model-value="modelValue"
                :label="`${campo.etichetta} (valore personalizzato)`" density="compact" variant="outlined"
                hide-details="auto" class="mt-2" clearable @update:model-value="$emit('update:modelValue', $event)"
                @click:clear="onRipristinaSelect">
                <template #prepend-inner>
                    <v-icon size="15" color="warning">mdi-pencil-outline</v-icon>
                </template>
            </v-text-field>
        </template>
        <!-- Autocomplete standard -->
        <v-autocomplete v-else :model-value="modelValue" :items="opzioniCampo" item-value="value" item-title="label"
            :label="campo.etichetta" :rules="rules" hide-details="auto" density="compact" variant="outlined"
            @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- ── checkbox singolo ─────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'checkbox'">
        <template v-if="campo.permettiValoreCustom">
            <v-checkbox :model-value="modalitaCustom ? false : modelValue" :label="campo.etichetta" hide-details
                density="compact" color="primary" class="mt-0" @update:model-value="onCheckboxConCustom" />
            <div v-if="modalitaCustom" class="d-flex align-center gap-2 mt-1">
                <v-text-field :model-value="modelValue === true || modelValue === false ? '' : modelValue"
                    :label="`${campo.etichetta} (valore personalizzato)`" density="compact" variant="outlined"
                    hide-details="auto" class="mt-0" clearable @update:model-value="$emit('update:modelValue', $event)"
                    @click:clear="onRipristinaSelect">
                    <template #prepend-inner>
                        <v-icon size="15" color="warning">mdi-pencil-outline</v-icon>
                    </template>
                </v-text-field>
                <v-btn size="x-small" variant="text" color="grey" @click="onRipristinaSelect">Annulla</v-btn>
            </div>
            <v-btn v-if="!modalitaCustom" size="x-small" variant="text" color="warning"
                prepend-icon="mdi-pencil-outline" class="mt-1" @click="modalitaCustom = true">
                Valore personalizzato
            </v-btn>
        </template>
        <v-checkbox v-else :model-value="modelValue" :label="campo.etichetta" hide-details density="compact"
            color="primary" class="mt-0" @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- ── radio group ──────────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'radio'">
        <div class="text-caption text-medium-emphasis mb-1">{{ campo.etichetta }}</div>
        <template v-if="campo.permettiValoreCustom">
            <v-radio-group :model-value="modalitaCustom ? VALORE_ALTRO : modelValue" hide-details density="compact"
                inline @update:model-value="onSelectConCustom">
                <v-radio v-for="opt in opzioniCampo" :key="opt.value" :label="opt.label" :value="opt.value"
                    color="primary" />
                <v-radio :label="ETICHETTA_ALTRO" :value="VALORE_ALTRO" color="warning" />
            </v-radio-group>
            <v-text-field v-if="modalitaCustom" :model-value="modelValue"
                :label="`${campo.etichetta} (valore personalizzato)`" density="compact" variant="outlined"
                hide-details="auto" class="mt-2" clearable @update:model-value="$emit('update:modelValue', $event)"
                @click:clear="onRipristinaSelect">
                <template #prepend-inner>
                    <v-icon size="15" color="warning">mdi-pencil-outline</v-icon>
                </template>
            </v-text-field>
        </template>
        <v-radio-group v-else :model-value="modelValue" hide-details density="compact" inline
            @update:model-value="$emit('update:modelValue', $event)">
            <v-radio v-for="opt in opzioniCampo" :key="opt.value" :label="opt.label" :value="opt.value"
                color="primary" />
        </v-radio-group>
    </template>

    <!-- ── checkbox group ───────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'checkboxgroup'">
        <div class="text-caption text-medium-emphasis mb-1">{{ campo.etichetta }}</div>
        <div class="d-flex flex-wrap gap-2">
            <v-checkbox v-for="opt in opzioniCampo" :key="opt.value" :label="opt.label"
                :model-value="(modelValue || []).includes(opt.value)" hide-details density="compact" color="primary"
                class="mt-0" @update:model-value="toggleCheckboxGroup(opt.value, $event)" />
        </div>
        <template v-if="campo.permettiValoreCustom">
            <v-btn v-if="!modalitaCustom" size="x-small" variant="text" color="warning"
                prepend-icon="mdi-pencil-outline" class="mt-1" @click="modalitaCustom = true">
                Valore personalizzato
            </v-btn>
            <div v-if="modalitaCustom" class="d-flex align-center gap-2 mt-2">
                <v-text-field :model-value="valoreCustomCheckboxGroup" label="Altro valore" density="compact"
                    variant="outlined" hide-details="auto" clearable @update:model-value="onCustomCheckboxGroupInput"
                    @click:clear="onRipristinaSelect">
                    <template #prepend-inner>
                        <v-icon size="15" color="warning">mdi-pencil-outline</v-icon>
                    </template>
                </v-text-field>
                <v-btn size="x-small" variant="text" color="grey" @click="onRipristinaSelect">Annulla</v-btn>
            </div>
        </template>
    </template>

    <!-- ── fallback: text ───────────────────────────────────────────────────── -->
    <v-text-field v-else :model-value="modelValue" :label="campo.etichetta" :rules="rules" hide-details="auto"
        density="compact" variant="outlined" @update:model-value="$emit('update:modelValue', $event)" />
</template>

<script>
import { computed, ref, watch } from 'vue'

const VALORE_ALTRO = '__altro__'
const ETICHETTA_ALTRO = 'Altro...'

// Tipi che supportano "Altro"
const TIPI_CON_CUSTOM = ['select', 'autocomplete', 'radio', 'checkbox', 'checkboxgroup']

// Mappa CampoKey → icona MDI
const ICONE_CAMPO = {
    NumeroCommessa: 'mdi-pound',
    Data: 'mdi-calendar-today-outline',
    DataConsegna: 'mdi-calendar-check-outline',
    ViaCliente: 'mdi-map-marker-outline',
    ViaCantiere: 'mdi-map-marker-outline',
    EmailCliente: 'mdi-email-outline',
    TelefonoCliente: 'mdi-phone-outline',
    PiCfCliente: 'mdi-card-text-outline',
}

// Mappa CampoKey → chiave nelle opzioni parametri
const CAMPO_OPZIONI_KEY = {
    Essenza: 'essenze',
    Serie: 'serie',
    Profilo: 'profili',
    Spessore: 'spessori',
    Verniciatura: 'verniciature',
    Persiana: 'persiane',
    VerniciaturaPersiana: 'verniciature',
    Avvolgibili: 'avvolgibili',
    Antone: 'antoine',
    ZanzarieraTipo: 'zanzariereTipo',
    ZanzarieraColore: 'zanzariereColore',
    TipoCommessa: 'tipi',
}

export default {
    name: 'DynamicField',

    props: {
        campo: { type: Object, required: true },
        modelValue: { default: null },
        opzioni: { type: Object, default: () => ({}) },
        rules: { type: Array, default: () => [] }
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {

        const isTextField = computed(() =>
            ['text', 'number', 'date'].includes(props.campo.tipoInput)
        )

        const inputType = computed(() => {
            if (props.campo.tipoInput === 'number') return 'number'
            if (props.campo.tipoInput === 'date') return 'date'
            return 'text'
        })

        const iconaCampo = computed(() => ICONE_CAMPO[props.campo.campoKey] || null)

        // Opzioni per select/autocomplete/radio/checkboxgroup
        const opzioniCampo = computed(() => {
            const key = CAMPO_OPZIONI_KEY[props.campo.campoKey]
            if (key && props.opzioni[key]) return props.opzioni[key]
            return []
        })

        // Opzioni con "Altro..." aggiunto in fondo (solo se permettiValoreCustom)
        const opzioniConAltro = computed(() => [
            ...opzioniCampo.value,
            { value: VALORE_ALTRO, label: ETICHETTA_ALTRO }
        ])

        // ── Logica modalità custom ────────────────────────────────────────────
        // Un valore è "custom" se non è null/undefined/false/true
        // e non è presente nell'elenco delle opzioni
        const isValoreCustom = computed(() => {
            const val = props.modelValue
            if (val === null || val === undefined || val === '' || val === false || val === true) return false
            if (!props.campo.permettiValoreCustom) return false
            if (!TIPI_CON_CUSTOM.includes(props.campo.tipoInput)) return false
            // Per checkboxgroup il valore è un array
            if (props.campo.tipoInput === 'checkboxgroup') {
                if (!Array.isArray(val)) return false
                return val.some(v => !opzioniCampo.value.find(o => o.value === v))
            }
            return !opzioniCampo.value.find(o => o.value === val)
        })

        const modalitaCustom = ref(isValoreCustom.value)

        // Quando cambia il valore dall'esterno (es. caricamento ordine in modifica)
        // attiviamo la modalità custom se necessario, ma NON la disattiviamo mai
        // durante l'editing (l'utente potrebbe aver svuotato il campo e stare riscrivendo)
        watch(() => props.modelValue, (newVal) => {
            // Attiviamo solo se il valore è effettivamente custom
            if (isValoreCustom.value) {
                modalitaCustom.value = true
            }
            // Disattiviamo solo se il valore cambia da esterno con un valore
            // presente nell'elenco (es. reset form o caricamento nuovo ordine),
            // NON se l'utente ha semplicemente svuotato il campo testo
            if (!isValoreCustom.value && !modalitaCustom.value) {
                const valoreNelleOpzioni = opzioniCampo.value.find(o => o.value === newVal)
                if (valoreNelleOpzioni) {
                    // È un valore valido dell'elenco arrivato dall'esterno: esci dalla modalità custom
                    modalitaCustom.value = false
                }
                // Se è null/'' durante editing: non fare nulla, lascia modalitaCustom = true
            }
        }, { immediate: true })

        // Gestisce la selezione dalla select/autocomplete/radio
        const onSelectConCustom = (val) => {
            if (val === VALORE_ALTRO) {
                modalitaCustom.value = true
                // Svuota il valore così la textbox è vuota
                emit('update:modelValue', '')
            } else {
                modalitaCustom.value = false
                emit('update:modelValue', val)
            }
        }

        // Ripristina la select (torna a modalità normale)
        const onRipristinaSelect = () => {
            modalitaCustom.value = false
            emit('update:modelValue', null)
        }

        // Gestione checkbox singolo con custom
        const onCheckboxConCustom = (val) => {
            if (val) {
                // Se spunta la checkbox normalmente
                modalitaCustom.value = false
                emit('update:modelValue', val)
            }
        }

        // Gestione checkboxgroup: il valore custom è un valore aggiuntivo libero
        // che viene aggiunto/rimosso dall'array
        const valoreCustomCheckboxGroup = computed(() => {
            if (!Array.isArray(props.modelValue)) return ''
            return props.modelValue.find(v => !opzioniCampo.value.find(o => o.value === v)) ?? ''
        })

        const onCustomCheckboxGroupInput = (nuovoCustom) => {
            const base = Array.isArray(props.modelValue)
                ? props.modelValue.filter(v => opzioniCampo.value.find(o => o.value === v))
                : []
            if (nuovoCustom) {
                emit('update:modelValue', [...base, nuovoCustom])
            } else {
                emit('update:modelValue', base)
            }
        }

        // Toggle per checkboxgroup (valore array) — standard
        const toggleCheckboxGroup = (value, checked) => {
            const current = Array.isArray(props.modelValue) ? [...props.modelValue] : []
            if (checked) {
                if (!current.includes(value)) current.push(value)
            } else {
                const idx = current.indexOf(value)
                if (idx > -1) current.splice(idx, 1)
            }
            emit('update:modelValue', current)
        }

        return {
            VALORE_ALTRO,
            ETICHETTA_ALTRO,
            isTextField,
            inputType,
            iconaCampo,
            opzioniCampo,
            opzioniConAltro,
            modalitaCustom,
            onSelectConCustom,
            onRipristinaSelect,
            onCheckboxConCustom,
            valoreCustomCheckboxGroup,
            onCustomCheckboxGroupInput,
            toggleCheckboxGroup
        }
    }
}
</script>